'use client';

import { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface LegalAcknowledgementModalProps {
  userId: string;
  onAccepted: () => void;
}

export default function LegalAcknowledgementModal({ userId, onAccepted }: LegalAcknowledgementModalProps) {
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  const handleAccept = async () => {
    if (!agreed) return;
    setIsLoading(true);
    setError(null);

    try {
      const now = new Date().toISOString();
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

      // Insert into legal_acknowledgements
      const { error: insertError } = await supabase
        .from('legal_acknowledgements')
        .insert({
          user_id: userId,
          legal_version: '1.0',
          accepted_at: now,
          ip_address: null, // IP not available client-side
          user_agent: userAgent,
        });

      if (insertError) throw insertError;

      // Update profiles
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          legal_accepted: true,
          legal_version: '1.0',
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      onAccepted();
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  // Prevent closing on backdrop click or ESC (handled by not having onClose)
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.preventDefault();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-14 h-14 bg-orange-50 rounded-full mx-auto mb-5">
          <svg className="w-7 h-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
        </div>

        <h2 id="legal-modal-title" className="text-xl font-bold text-slate-900 text-center mb-2">
          Legal Acknowledgement
        </h2>
        <p className="text-sm text-slate-500 text-center mb-6">
          Before you continue, please review and accept our terms.
        </p>

        {/* Legal text summary */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-6 text-sm text-slate-600 space-y-2 max-h-40 overflow-y-auto">
          <p>
            By using Incade, you agree to our{' '}
            <a href="#" className="text-orange-500 hover:underline font-medium">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-orange-500 hover:underline font-medium">Privacy Policy</a>.
          </p>
          <p>
            We collect and process your advertising data to provide insights and recommendations. Your data is handled securely and never sold to third parties.
          </p>
          <p>
            You can withdraw consent at any time by contacting support. Continued use of the platform constitutes acceptance of these terms (version 1.0).
          </p>
        </div>

        {/* Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer mb-6 group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                agreed
                  ? 'bg-orange-500 border-orange-500' :'bg-white border-slate-300 group-hover:border-orange-400'
              }`}
            >
              {agreed && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm text-slate-700 leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-orange-500 hover:underline font-medium">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-orange-500 hover:underline font-medium">Privacy Policy</a>
          </span>
        </label>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Accept button */}
        <button
          type="button"
          onClick={handleAccept}
          disabled={!agreed || isLoading}
          className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing...
            </>
          ) : (
            'Accept & Continue'
          )}
        </button>

        <p className="mt-3 text-center text-xs text-slate-400">
          This acknowledgement is required to use Incade.
        </p>
      </div>
    </div>
  );
}
